import React, { useRef } from "react";

import {
  emitRecommenderRequest,
  FromProduct,
  SOURCE_TYPE_PRODUCT_PAGE_RECOMMENDER_BI_SIMILAR_PRODUCT_DESC_ONLY,
  SOURCE_TYPE_PRODUCT_PAGE_RECOMMENDER_BI_SIMILAR_PRODUCT_IMAGE_CAT,
  SOURCE_TYPE_PRODUCT_PAGE_RECOMMENDER_BI_SIMILAR_PRODUCT_IMAGE_DESC,
  SOURCE_TYPE_PRODUCT_PAGE_RECOMMENDER_BI_SIMILAR_PRODUCT_IMAGE_ONLY,
} from "@catchoftheday/analytics";

import {
  ModelType,
  SimilarProductsQuery,
  useSimilarProductsQuery,
} from "@personalisation/generated/graphql";

import { RecommenderCarousel } from "../RecommenderCarousel";
import {
  getProductTiles,
  getRecommenderTrackingData,
  handleRecommenderQueryUpdate,
  useVisibleRecommenderTiles,
} from "../utils";

const mapBucketToModelType = (experimentBucket: number): ModelType => {
  switch (experimentBucket) {
    case 1:
      return "IMAGE";
    case 2:
      return "IMAGE_AND_DESCRIPTION";
    case 3:
      return "IMAGE_AND_CATEGORY";
    default:
      return "DESCRIPTION";
  }
};

type SimilarProductsProps = {
  productId: string;
  adult: boolean;
  fromProduct: FromProduct;
  experimentBucket: number;
  sourceId?: string;
};

export const SimilarProducts = ({
  productId,
  adult,
  fromProduct,
  experimentBucket,
  sourceId,
}: SimilarProductsProps) => {
  const sourceType = (() => {
    switch (experimentBucket) {
      case 1:
        return SOURCE_TYPE_PRODUCT_PAGE_RECOMMENDER_BI_SIMILAR_PRODUCT_IMAGE_ONLY;
      case 2:
        return SOURCE_TYPE_PRODUCT_PAGE_RECOMMENDER_BI_SIMILAR_PRODUCT_IMAGE_DESC;
      case 3:
        return SOURCE_TYPE_PRODUCT_PAGE_RECOMMENDER_BI_SIMILAR_PRODUCT_IMAGE_CAT;
      default:
        return SOURCE_TYPE_PRODUCT_PAGE_RECOMMENDER_BI_SIMILAR_PRODUCT_DESC_ONLY;
    }
  })();

  const visibleTiles = useVisibleRecommenderTiles();
  const trackingVariables = useRef({
    offset: 0,
    limit: visibleTiles,
  });

  const { data, error, loading, fetchMore } = useSimilarProductsQuery({
    variables: {
      productId,
      limit: visibleTiles,
      offset: 0,
      adult,
      modelType: mapBucketToModelType(experimentBucket),
    },
    ssr: false,
    skip: !visibleTiles,
    notifyOnNetworkStatusChange: true,
    onCompleted: ({
      similarProducts: {
        metaData: {
          preHydrationCount,
          postHydrationCount,
          hydrationErrorCount,
        },
      },
    }) => {
      emitRecommenderRequest({
        source_type: sourceType,
        pre_hydration_product_count: preHydrationCount,
        post_hydration_product_count: postHydrationCount,
        hydration_error_count: hydrationErrorCount,
        page_offset: trackingVariables.current.offset,
        page_limit: trackingVariables.current.limit,
        ...fromProduct,
      });
    },
  });

  if (!data?.similarProducts.products.length || error) {
    return null;
  }

  const products = data.similarProducts.products;
  const { totalCount, nextItemIndex } = data.similarProducts.pagination;

  const tiles = getProductTiles({
    products,
    sourceType,
    sourceId,
    fromProduct,
  });
  const trackingData = getRecommenderTrackingData(products, "product");

  return (
    <RecommenderCarousel
      tiles={tiles}
      heading="Similar Products"
      sourceType={sourceType}
      sourceId={sourceId}
      trackingData={trackingData}
      fromProduct={fromProduct}
      paginate
      maxTiles={totalCount}
      carouselProps={{
        onSlideChange: ({ currentIndex, slidesPerView, direction }) => {
          const lastVisibleTile = currentIndex + slidesPerView - 1;
          if (direction === "forward" && !products[lastVisibleTile]) {
            trackingVariables.current = {
              offset: nextItemIndex,
              limit: slidesPerView,
            };
            fetchMore({
              variables: {
                offset: nextItemIndex,
              },
              updateQuery: (prevResult, { fetchMoreResult }) =>
                handleRecommenderQueryUpdate<SimilarProductsQuery>({
                  prevResult,
                  fetchMoreResult,
                  recommenderKey: "similarProducts",
                }),
            });
          }
        },
        disableNavButtons: {
          next: loading,
        },
        touchEnabled: !loading,
      }}
    />
  );
};
